/**
 * Phase 18.16-B Batch 3 — Info pages admin sub-section.
 *
 * Mirrors the Batch 2 (Tool pages) editor exactly. Slug picker +
 * section tabs (page_banner / intro / cta_band) per page.
 *
 * Backend: /api/admin/info-pages/{slug}/*
 *
 * Editor UX rules (PRD R4): all wrapper sub-components are defined at
 * top level → stable identity → no focus-loss on keystrokes. Controlled
 * inputs default to "" so React never sees undefined.
 */
import React, { useCallback, useEffect, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, Loader2, FileText, Receipt, Headphones,
} from "lucide-react";

const SLUGS = [
  { value: "documenten",      label: "Documenten",       icon: FileText },
  { value: "btw-terugvragen", label: "BTW-teruggave",    icon: Receipt },
  { value: "contact",         label: "Contact",          icon: Headphones },
];

const ICON_OPTIONS = [
  "ship", "plane", "package", "truck", "car", "map", "briefcase", "user",
  "check", "clock", "shield", "banknote", "support", "info", "receipt", "zap",
];

const SECTIONS = [
  { key: "page_banner", label: "Page banner" },
  { key: "intro",       label: "Intro" },
  { key: "cta_band",    label: "CTA-band" },
];

function sectionLabel(key) {
  return (SECTIONS.find((s) => s.key === key) || {}).label || key;
}

export default function InfoPagesSubSection() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("website:edit") || hasPermission("admin:all");

  const [slug, setSlug] = useState(SLUGS[0].value);
  const [activeSection, setActiveSection] = useState("page_banner");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState(null);

  const load = useCallback(async (newSlug) => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/info-pages/${newSlug}`);
      setData(r.data || null);
    } catch (e) {
      toast.error(formatApiError(e, "Kon instellingen niet laden"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(slug); }, [load, slug]);

  const setField = (sec, k, v) => setData((d) => ({
    ...d,
    sections: { ...d.sections, [sec]: { ...(d.sections[sec] || {}), [k]: v } },
  }));
  const setListField = (sec, listKey, idx, k, v) => setData((d) => {
    const list = [...(d.sections[sec]?.[listKey] || [])];
    list[idx] = { ...(list[idx] || {}), [k]: v };
    return { ...d, sections: { ...d.sections, [sec]: { ...(d.sections[sec] || {}), [listKey]: list } } };
  });
  const addListItem = (sec, listKey, template) => setData((d) => {
    const list = [...(d.sections[sec]?.[listKey] || [])];
    list.push({ ...template });
    return { ...d, sections: { ...d.sections, [sec]: { ...(d.sections[sec] || {}), [listKey]: list } } };
  });
  const removeListItem = (sec, listKey, idx) => setData((d) => {
    const list = [...(d.sections[sec]?.[listKey] || [])];
    list.splice(idx, 1);
    return { ...d, sections: { ...d.sections, [sec]: { ...(d.sections[sec] || {}), [listKey]: list } } };
  });

  const saveSection = async (sec) => {
    if (!canEdit) return;
    setSavingSection(sec);
    try {
      const r = await adminApi.put(`/admin/info-pages/${slug}/${sec}`, {
        payload: data.sections[sec] || {},
      });
      if (r.data) setData(r.data);
      toast.success(`${sectionLabel(sec)} opgeslagen`);
    } catch (e) {
      toast.error(formatApiError(e, "Opslaan mislukt"));
    } finally {
      setSavingSection(null);
    }
  };

  if (loading || !data) {
    return (
      <div className="py-10 text-center text-gray-400 text-sm">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
        Informatiepagina laden...
      </div>
    );
  }

  const activeSec = data.sections[activeSection] || {};
  const isVat = slug === "btw-terugvragen";

  return (
    <div data-testid="info-pages-sub-section">
      {!canEdit && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
          U heeft alleen leesrechten — wijzigen vereist Owner/Admin of Content Manager.
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-6">
        {SLUGS.map((s) => {
          const Icon = s.icon;
          const active = slug === s.value;
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => { setSlug(s.value); setActiveSection("page_banner"); }}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${active ? "bg-[#082c50] text-white" : "bg-slate-100 text-[#082c50] hover:bg-slate-200"}`}
              data-testid={`info-page-slug-${s.value}`}
            >
              <Icon className="w-4 h-4" /> {s.label}
            </button>
          );
        })}
      </div>

      {isVat && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 leading-relaxed">
          <strong>Belangrijk — juridisch veilige teksten:</strong> 5999Cargo
          geeft géén fiscaal advies. Vermijd uitspraken als
          “u krijgt altijd BTW terug”. Gebruik formuleringen zoals
          <em> “controleer altijd bij uw leverancier of de Belastingdienst
          of u recht heeft op BTW-teruggave”</em>.
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4 border-b border-slate-200 pb-3">
        {SECTIONS.map((sec) => (
          <button
            key={sec.key}
            type="button"
            onClick={() => setActiveSection(sec.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${activeSection === sec.key ? "bg-[#082c50] text-white" : "bg-slate-100 text-[#082c50] hover:bg-slate-200"}`}
            data-testid={`info-page-section-${sec.key}`}
          >
            {sec.label}
          </button>
        ))}
      </div>

      <SectionForm
        sectionKey={activeSection}
        values={activeSec}
        slug={slug}
        canEdit={canEdit}
        saving={savingSection === activeSection}
        onSave={() => saveSection(activeSection)}
        onChange={(k, v) => setField(activeSection, k, v)}
        onListChange={(listKey, idx, k, v) => setListField(activeSection, listKey, idx, k, v)}
        onListAdd={(listKey, tpl) => addListItem(activeSection, listKey, tpl)}
        onListRemove={(listKey, idx) => removeListItem(activeSection, listKey, idx)}
      />

      <p className="mt-4 text-xs text-gray-500 leading-relaxed">
        Wijzigingen verschijnen direct op de publieke pagina. Documenten,
        downloads, contactformulier, offerteformulier-anchor en
        company contact instellingen blijven volledig ongewijzigd.
      </p>
    </div>
  );
}

function SectionForm({
  sectionKey, values, slug, canEdit, saving, onSave,
  onChange, onListChange, onListAdd, onListRemove,
}) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4"
      data-testid={`info-page-editor-${slug}-${sectionKey}`}
    >
      <div className="flex items-center justify-between">
        <h3 className="font-chivo font-bold text-lg text-[#082c50]">{sectionLabel(sectionKey)}</h3>
        <Button
          onClick={onSave}
          disabled={!canEdit || saving}
          className="rounded-xl bg-[#082c50] hover:bg-[#0a335c] text-white"
          data-testid={`info-page-save-${sectionKey}`}
        >
          {saving
            ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Opslaan...</>
            : <><Save className="w-4 h-4 mr-1" />Opslaan</>}
        </Button>
      </div>

      {sectionKey === "page_banner" && (
        <>
          <TextRow label="Eyebrow" v={values.eyebrow} onChange={(v) => onChange("eyebrow", v)} testid={`${slug}-banner-eyebrow`} disabled={!canEdit} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextRow label="Hoofdtitel" v={values.title} onChange={(v) => onChange("title", v)} testid={`${slug}-banner-title`} disabled={!canEdit} />
            <TextRow label="Highlight (gradient)" v={values.highlight} onChange={(v) => onChange("highlight", v)} testid={`${slug}-banner-highlight`} disabled={!canEdit} />
          </div>
          <AreaRow label="Subtitle" v={values.subtitle} onChange={(v) => onChange("subtitle", v)} testid={`${slug}-banner-subtitle`} disabled={!canEdit} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextRow label="Primary CTA label" v={values.cta_primary_label} onChange={(v) => onChange("cta_primary_label", v)} disabled={!canEdit} />
            <TextRow label="Primary CTA URL" v={values.cta_primary_url} onChange={(v) => onChange("cta_primary_url", v)} disabled={!canEdit} />
            <TextRow label="Secondary CTA label" v={values.cta_secondary_label} onChange={(v) => onChange("cta_secondary_label", v)} disabled={!canEdit} />
            <TextRow label="Secondary CTA URL" v={values.cta_secondary_url} onChange={(v) => onChange("cta_secondary_url", v)} disabled={!canEdit} />
          </div>
          <ListEditor
            label="USP-badges (max 5)"
            items={values.badges || []}
            fields={[
              { key: "icon",  label: "Icon",  type: "select", options: ICON_OPTIONS },
              { key: "label", label: "Label", type: "text" },
            ]}
            onChange={(idx, f, v) => onListChange("badges", idx, f, v)}
            onAdd={() => onListAdd("badges", { icon: "shield", label: "" })}
            onRemove={(idx) => onListRemove("badges", idx)}
            canEdit={canEdit}
            max={5}
          />
        </>
      )}

      {sectionKey === "intro" && (
        <>
          <TextRow label="Eyebrow" v={values.eyebrow} onChange={(v) => onChange("eyebrow", v)} disabled={!canEdit} />
          <TextRow label="Sectietitel (H2)" v={values.title} onChange={(v) => onChange("title", v)} disabled={!canEdit} />
          <AreaRow label="Helper tekst / uitleg" v={values.body} onChange={(v) => onChange("body", v)} disabled={!canEdit} />
          <p className="text-xs text-gray-500 italic">
            Wordt op de publieke pagina pas getoond zodra de pagina-template
            de intro-sectie expliciet rendert (opt-in per pagina).
          </p>
        </>
      )}

      {sectionKey === "cta_band" && (
        <>
          <TextRow label="Titel" v={values.title} onChange={(v) => onChange("title", v)} disabled={!canEdit} />
          <AreaRow label="Beschrijving" v={values.description} onChange={(v) => onChange("description", v)} disabled={!canEdit} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextRow label="Primary CTA label" v={values.cta_primary_label} onChange={(v) => onChange("cta_primary_label", v)} disabled={!canEdit} />
            <TextRow label="Primary CTA URL" v={values.cta_primary_url} onChange={(v) => onChange("cta_primary_url", v)} disabled={!canEdit} />
            <TextRow label="Secondary CTA label" v={values.cta_secondary_label} onChange={(v) => onChange("cta_secondary_label", v)} disabled={!canEdit} />
            <TextRow label="Secondary CTA URL" v={values.cta_secondary_url} onChange={(v) => onChange("cta_secondary_url", v)} disabled={!canEdit} />
          </div>
          <p className="text-xs text-gray-500 italic">
            Laat de titel leeg om de hele CTA-band te verbergen op deze pagina.
          </p>
        </>
      )}
    </div>
  );
}

function TextRow({ label, v, onChange, testid, disabled }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input
        value={v ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        data-testid={testid ? `${testid}-input` : undefined}
      />
    </div>
  );
}

function AreaRow({ label, v, onChange, testid, disabled }) {
  return (
    <div>
      <Label>{label}</Label>
      <Textarea
        rows={3}
        value={v ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        data-testid={testid ? `${testid}-input` : undefined}
      />
    </div>
  );
}

function ListEditor({ label, items, fields, onChange, onAdd, onRemove, canEdit, max }) {
  return (
    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/50">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-[#082c50]">{label}</span>
        {canEdit && items.length < (max || 12) && (
          <Button size="sm" variant="outline" onClick={onAdd} className="rounded-lg text-xs">
            <Plus className="w-3 h-3 mr-1" /> Toevoegen
          </Button>
        )}
      </div>
      <div className="space-y-2">
        {items.map((it, idx) => (
          <div key={idx} className="flex flex-wrap items-end gap-2 p-2 bg-white rounded-lg border border-slate-200">
            {fields.map((f) => (
              <div key={f.key} className="flex-1 min-w-[140px]">
                <Label className="text-[10px] uppercase tracking-wide">{f.label}</Label>
                {f.type === "select" ? (
                  <select
                    value={it[f.key] || ""}
                    onChange={(e) => onChange(idx, f.key, e.target.value)}
                    disabled={!canEdit}
                    className="w-full h-9 px-2 rounded-lg border border-slate-200 text-sm"
                  >
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <Input
                    value={it[f.key] || ""}
                    onChange={(e) => onChange(idx, f.key, e.target.value)}
                    disabled={!canEdit}
                  />
                )}
              </div>
            ))}
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => onRemove(idx)} className="rounded-lg text-red-600">
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-xs text-gray-500 italic py-2">Nog geen items.</div>
        )}
      </div>
    </div>
  );
}
